import { LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react'
import { useNavigate } from 'react-router'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLogout, useMe } from '@/lib/auth'
import { t } from '@/lib/text'
import { isThemeChoice } from '@/lib/theme'
import { isPickerStyle } from '@/lib/pickerStyle'
import { usePickerStyle, useThemePreference } from '@/lib/useThemePreference'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export function UserMenu() {
  const me = useMe()
  const navigate = useNavigate()
  const logout = useLogout()
  const { choice, setChoice } = useThemePreference()
  const picker = usePickerStyle()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('core.shell.user_menu', { name: me.full_name })}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 max-md:min-h-11"
      >
        <span aria-hidden className="flex size-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initials(me.full_name)}
        </span>
        <span className="max-w-40 truncate text-sm font-medium max-md:hidden">{me.full_name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="text-foreground">
          <span className="block truncate font-semibold">{me.full_name}</span>
          <span className="block truncate font-normal text-muted-foreground">{me.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/me')}>
          <UserRound aria-hidden />
          {t('core.shell.my_details')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('core.shell.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={choice} onValueChange={(value) => isThemeChoice(value) && setChoice(value)}>
          <DropdownMenuRadioItem value="system">
            <Monitor aria-hidden />
            {t('core.shell.theme_system')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">
            <Sun aria-hidden />
            {t('core.shell.theme_light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon aria-hidden />
            {t('core.shell.theme_dark')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('core.shell.picker')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={picker.choice} onValueChange={(value) => isPickerStyle(value) && picker.setChoice(value)}>
          <DropdownMenuRadioItem value="standard">{t('core.shell.picker_standard')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="sleek">{t('core.shell.picker_sleek')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="sleeker">{t('core.shell.picker_sleeker')}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut aria-hidden />
          {t('core.shell.log_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
